import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Download } from "lucide-react";
import { getCurrentBaselineRolloutSummary, getPreviousActiveRolloutSummary } from "../../lib/api";
import { PageLoader } from "../../components/ui/spinner";



function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatPublishedDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

// WP-RPT-05 (FMP-NV-014): a version carrying a non-zero Failed count is flagged
// for investigation. The service reports needs_investigation; fall back to the
// count itself so the flag also works against a service that predates the field.
function needsInvestigation(summary, failed) {
  return typeof summary?.needs_investigation === "boolean" ? summary.needs_investigation : failed > 0;
}

// The FAILED figure is red only when there is something to investigate; a zero
// used to render in the same bold red as 47, so the colour carried no signal.
function failedColor(flagged) {
  return flagged ? "text-red-600" : "text-slate-400";
}

// Which breakdown page a version card opens, or undefined when the card is not
// navigable. Hoisted out of the onClick prop, where it was a three-way nested
// ternary (S3358).
function cardNavHandler(v, navigate) {
  if (v.isCurrentBaseline) return () => navigate("/rollouts/current-baseline");
  if (v.isPreviousActive) return () => navigate("/rollouts/previous-active");
  return undefined;
}

export default function RolloutsPage() {
  const navigate = useNavigate();
  const [currentBaseline, setCurrentBaseline] = useState(null);
  const [previousActive, setPreviousActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchRolloutSummary = async () => {
      setLoading(true);
      // allSettled, not all: these are independent cards, and with Promise.all
      // a single rejection reached one catch that nulled every one of them.
      // One slow endpoint therefore blanked the entire page - the current
      // baseline card read 0/0/0 while its own request had returned a full
      // fleet. Each card now succeeds or fails on its own request.
      const [current, previous] = await Promise.allSettled([
        getCurrentBaselineRolloutSummary(),
        getPreviousActiveRolloutSummary(),
      ]);
      if (!mounted) return;

      const settle = (result, label) => {
        if (result.status === "fulfilled") return result.value ?? null;
        console.error(`[RolloutsPage] ${label} failed`, result.reason);
        return null;
      };

      setCurrentBaseline(settle(current, "current baseline"));
      setPreviousActive(settle(previous, "previous active"));
      setLoading(false);
    };

    fetchRolloutSummary();
    return () => {
      mounted = false;
    };
  }, []);

  const versions = useMemo(() => {
    const previousActiveVersion = previousActive?.previous_active || "--";
    const previousActiveTotal = toNumber(previousActive?.total, 0);
    const previousActiveReceived = toNumber(previousActive?.received_it, 0);
    const previousActiveSkipped = toNumber(previousActive?.skipped, 0);
    const previousActiveFailed = toNumber(previousActive?.failed, 0);
    const peakReceivedPercent = Math.max(0, Math.min(100, toNumber(previousActive?.peak_received_percent, 0)));
    const previousPublishedDate = formatPublishedDate(previousActive?.published_at);

    // The counts are now scored against that rollout's COHORT, not the fleet,
    // so the footer must not keep saying "of fleet" - a rollout that targeted
    // 20 of 53 devices and reached all 20 is 100% received, not 38%.
    const noPreviousRollout = previousActive?.has_previous_rollout === false;
    const previousActiveFlagged = needsInvestigation(previousActive, previousActiveFailed);
    const previousActiveCard = {
      version: noPreviousRollout ? "No previous rollout" : previousActiveVersion,
      label: "Previous Active",
      labelColor: "text-slate-400",
      borderColor: previousActiveFlagged ? "border-red-300" : "border-slate-200",
      failedCount: previousActiveFailed,
      flagged: previousActiveFlagged,
      stats: [
        { value: String(previousActiveTotal), label: "TOTAL", color: "text-slate-900" },
        { value: String(previousActiveReceived), label: "RECEIVED IT", color: "text-teal-600" },
        { value: String(previousActiveSkipped), label: "SKIPPED", color: "text-slate-500" },
        { value: String(previousActiveFailed), label: "FAILED", color: failedColor(previousActiveFlagged) },
      ],
      progress: peakReceivedPercent,
      progressColor: "bg-teal-500",
      progressBg: "bg-slate-200",
      footer: {
        left: noPreviousRollout
          ? "No earlier rollout to compare against"
          : `${peakReceivedPercent.toFixed(1)}% of the ${previousActiveTotal}-device rollout received ${previousActiveVersion} at peak`,
        right: previousPublishedDate ? `Published ${previousPublishedDate}` : "",
      },
      deprecated: false,
      isPreviousActive: true,
      key: previousActive?.firmware_id || previousActiveVersion,
    };

    const total = toNumber(currentBaseline?.total, 0);
    const updated = toNumber(currentBaseline?.updated, 0);
    const pending = toNumber(currentBaseline?.pending, 0);
    const mandatoryWait = toNumber(currentBaseline?.mandatory_wait, 0);
    const failed = toNumber(currentBaseline?.failed, 0);
    const completionPercent = Math.max(0, Math.min(100, toNumber(currentBaseline?.completion_percent, 0)));
    const publishedDate = formatPublishedDate(currentBaseline?.published_at);
    const version = currentBaseline?.current_baseline || "--";
    // The API reports active_rollout: false when no firmware is in the Rollout
    // state. That is a real answer, not a failure - it means nothing is being
    // distributed to devices right now - and it must not look like the card
    // simply failed to load. It is distinguishable from a load failure because
    // a failure leaves currentBaseline null and this flag undefined.
    const noActiveRollout = currentBaseline?.active_rollout === false;
    const rolloutTier = currentBaseline?.rollout_tier || "full";
    const deviceWord = total === 1 ? "device" : "devices";
    const currentFlagged = needsInvestigation(currentBaseline, failed);
    let currentBorder = "border-green-400";
    if (noActiveRollout) currentBorder = "border-slate-300";
    else if (currentFlagged) currentBorder = "border-red-400";
    const currentBaselineCard = {
      version: noActiveRollout ? "No active rollout" : version,
      noActiveRollout,
      label: "Current Baseline",
      labelColor: "text-slate-600",
      borderColor: currentBorder,
      failedCount: failed,
      flagged: currentFlagged,
      stats: [
        { value: String(total), label: "TOTAL", color: "text-slate-900" },
        { value: String(updated), label: "UPDATED", color: "text-green-600" },
        { value: String(pending), label: "PENDING", color: "text-amber-500" },
        { value: String(mandatoryWait), label: "MANDATORY WAIT", color: "text-red-500" },
        { value: String(failed), label: "FAILED", color: failedColor(currentFlagged) },
      ],
      progress: completionPercent,
      progressColor: "bg-green-500",
      progressBg: "bg-slate-200",
      footer: {
        // The tier and cohort size are shown because TOTAL is now the rollout's
        // cohort, not the fleet. Without them a canary of 1 reads as a fleet of
        // 1 - a stalled rollout rather than a deliberately small one.
        left: noActiveRollout
          ? "No firmware is currently rolling out"
          : `${completionPercent.toFixed(2)}% complete · ${rolloutTier} rollout to ${total} ${deviceWord}`,
        right: publishedDate ? `Published ${publishedDate}` : "",
      },
      deprecated: false,
      isCurrentBaseline: true,
      key: currentBaseline?.firmware_id || version,
    };

    // Deprecated firmware is deliberately NOT shown here. This page is about
    // what is rolling out now and what rolled out before it; a retired build is
    // neither. It keeps its own detail page at /rollouts/deprecated/:id, and
    // not fetching it here is one fewer request that can fail on this page.
    return [currentBaselineCard, previousActiveCard];
  }, [currentBaseline, previousActive]);

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Rollout Summary</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/telemetry")}
            className="text-sm font-semibold px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Compliance Table
          </button>
          <button
            onClick={() => navigate("/telemetry/export")}
            className="flex items-center gap-2 bg-[#08148C] hover:bg-[#0d1fa8] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {/* Version cards */}
      <div className="space-y-4">
        {versions.map((v) => (
          <React.Fragment key={v.key || v.version}>
            <div // NOSONAR S6848/S1082: the version card is only conditionally interactive (cardNavHandler returns undefined for non-navigable versions), so a <button> would put inert cards in the tab order; role="button" would only trade S6848 for S6819.
              className={`bg-white border-2 ${v.borderColor} rounded-xl px-6 py-5 ${v.isCurrentBaseline || v.isPreviousActive ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}
              onClick={cardNavHandler(v, navigate)}
            >
              {/* Card header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold text-slate-900">{v.version}</span>
                  {v.label && (
                    <span className={`text-sm ${v.labelColor}`}>{v.label}</span>
                  )}
                  {v.flagged && (
                    // The card itself opens the breakdown page, which lists the
                    // failed devices - so the badge is a prompt, not a second button.
                    <span
                      role="status"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-0.5"
                    >
                      <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                      {v.failedCount} failed — investigate
                    </span>
                  )}
                </div>
                {(v.isCurrentBaseline || v.isPreviousActive) && <span className="text-xs text-slate-500 font-medium">View details</span>}
              </div>

              {/* Stats row */}
              {!v.deprecated && v.stats.length > 0 && (
                <div className="flex items-end gap-8 mb-4">
                  {v.stats.map((s) => (
                    <div key={s.label}>
                      <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mt-0.5">
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Progress bar */}
              <div className={`w-full h-2.5 rounded-full ${v.progressBg} overflow-hidden mb-2`}>
                <div
                  className={`h-full rounded-full ${v.progressColor} transition-all`}
                  style={{ width: `${v.progress}%` }}
                />
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">{v.footer.left}</span>
                {v.footer.right && (
                  <span className="text-xs text-slate-400">{v.footer.right}</span>
                )}
              </div>
            </div>

          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

